import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import {
  Plus, X, Trash2, Receipt, FileText, Wallet, PieChart, TrendingUp, TrendingDown, Landmark,
  AlertCircle, Truck, Building2, ScrollText, Pencil,
} from "lucide-react";
import { fmtMoney, CURRENCIES } from "@/lib/money";
import { PdfLangMenu } from "@/components/PdfLangMenu";
import { invoicePDF } from "@/lib/invoicePdf";
import { downloadVoucher } from "@/lib/voucherPdf";
import { SuppliersTab } from "@/components/accounting/SuppliersTab";
import { AgingTab } from "@/components/accounting/AgingTab";
import { BranchesTab } from "@/components/accounting/BranchesTab";
import { StatementTab } from "@/components/accounting/StatementTab";
import PhotoUploader from "@/components/PhotoUploader";
import AttachmentThumb from "@/components/AttachmentThumb";

const CATEGORIES = ["combustible", "alquiler", "salarios", "aduanas", "transporte", "suministros", "otros"];
const METHODS = ["transferencia", "efectivo", "tarjeta", "bizum", "cheque"];
const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_EXPENSE = { fecha: today(), categoria: "otros", descripcion: "", proveedor: "", monto: 0, moneda: "EUR", iva_pct: 21, metodo_pago: "transferencia", oficina: "", notas: "", photo_ids: [] };
const EMPTY_INVOICE = { client_id: "", cliente_nombre: "", cliente_tax_id: "", cliente_direccion: "", fecha: today(), vencimiento: "", moneda: "EUR", oficina: "", metodo_pago: "transferencia", descuento: 0, descuento_tipo: "amount", notas: "", remarks: "", lineas: [{ descripcion: "", cantidad: 1, precio_unitario: 0, iva_pct: 21 }], photo_ids: [] };
const EMPTY_PAYMENT = { invoice_id: "", fecha: today(), monto: 0, moneda: "EUR", metodo: "transferencia", referencia: "", oficina: "", notas: "", photo_ids: [] };

const CurrencySelect = ({ value, onChange, testid, disabled = false }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-600 disabled:bg-slate-100">
    {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} · {c.symbol}</option>)}
  </select>
);

const Stat = ({ icon: Icon, label, value, tone, testid }) => (
  <div className={`rounded-xl border p-4 ${tone}`} data-testid={testid}>
    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider opacity-70">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className="mt-1.5 text-2xl font-semibold tabular-nums">{value}</div>
  </div>
);

export default function Accounting() {
  const { api, user, token } = useAuth();
  const { t } = useI18n();
  const isGlobal = user.role === "admin" || user.role === "contable";

  const [tab, setTab] = useState("summary");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [oficinaF, setOficinaF] = useState("");
  const [oficinas, setOficinas] = useState([]);
  const [clients, setClients] = useState([]);

  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);

  const [modal, setModal] = useState(null); // 'expense' | 'invoice' | 'payment'
  const [expForm, setExpForm] = useState(EMPTY_EXPENSE);
  const [invForm, setInvForm] = useState(EMPTY_INVOICE);
  const [payForm, setPayForm] = useState(EMPTY_PAYMENT);
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState({});
  const [editingInvoice, setEditingInvoice] = useState(null);

  const params = useMemo(() => {
    const p = {};
    if (desde) p.desde = desde;
    if (hasta) p.hasta = hasta;
    if (oficinaF && isGlobal) p.oficina = oficinaF;
    return p;
  }, [desde, hasta, oficinaF, isGlobal]);

  const load = useCallback(async () => {
    try {
      const [s, e, i, p] = await Promise.all([
        api.get("/accounting/summary", { params }),
        api.get("/expenses", { params }),
        api.get("/invoices", { params }),
        api.get("/payments", { params }),
      ]);
      setSummary(s.data); setExpenses(e.data); setInvoices(i.data); setPayments(p.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  }, [api, params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isGlobal) api.get("/oficinas").then(r => setOficinas(r.data.oficinas || [])).catch(() => {});
    api.get("/clientes").then(r => setClients(r.data || [])).catch(() => {});
    api.get("/settings/invoice").then(r => setSettings(r.data || {})).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const cur = summary?.base || "EUR";
  const money = (v, m) => fmtMoney(v, m || cur);
  const [rates, setRates] = useState({});
  useEffect(() => { if (summary?.rates) setRates(summary.rates); }, [summary]);

  const saveRates = async () => {
    try {
      await api.put("/settings/currencies", { base: cur, rates: Object.fromEntries(Object.entries(rates).map(([k, v]) => [k, +v])) });
      toast.success(t("saved")); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const saveExpense = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/expenses", expForm);
      toast.success(t("saved")); setModal(null); setExpForm(EMPTY_EXPENSE); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const saveInvoice = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editingInvoice) {
        await api.patch(`/invoices/${editingInvoice}`, invForm);
        toast.success(t("saved"));
      } else {
        const { data } = await api.post("/invoices", invForm);
        toast.success(`${t("saved")} · ${data.numero}`);
      }
      setModal(null); setEditingInvoice(null); setInvForm(EMPTY_INVOICE); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const openInvoiceEdit = (inv) => {
    setEditingInvoice(inv.id);
    setInvForm({
      client_id: inv.client_id || "", cliente_nombre: inv.cliente_nombre || "",
      cliente_tax_id: inv.cliente_tax_id || "", cliente_direccion: inv.cliente_direccion || "",
      fecha: inv.fecha || today(), vencimiento: inv.vencimiento || "", oficina: inv.oficina || "",
      moneda: inv.moneda || "EUR",
      metodo_pago: inv.metodo_pago || "transferencia",
      descuento: +inv.descuento || 0,
      descuento_tipo: inv.descuento_tipo || "amount",
      notas: inv.notas || "", remarks: inv.remarks || "",
      photo_ids: inv.photo_ids || [],
      lineas: (inv.lineas || []).map(l => ({ ...l })),
    });
    setModal("invoice");
  };

  const savePayment = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/payments", payForm);
      toast.success(t("saved")); setModal(null); setPayForm(EMPTY_PAYMENT); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const del = async (kind, id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/${kind}/${id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openInvoices = useMemo(() => invoices.filter(i => i.estado !== "pagada"), [invoices]);

  const invTotals = useMemo(() => {
    // Mirrors backend _invoice_totals: discount applies to base BEFORE VAT.
    const sub = invForm.lineas.reduce((a, l) => a + (+l.cantidad || 0) * (+l.precio_unitario || 0), 0);
    const descInput = Math.max(0, +invForm.descuento || 0);
    const desc = invForm.descuento_tipo === "percent"
      ? Math.min(sub, +(sub * descInput / 100).toFixed(2))
      : Math.min(sub, descInput);
    const base = Math.max(0, sub - desc);
    const baseRatio = sub > 0 ? base / sub : 0;
    const iva = invForm.lineas.reduce(
      (a, l) => a + (+l.cantidad || 0) * (+l.precio_unitario || 0) * baseRatio * (+l.iva_pct || 0) / 100,
      0
    );
    return { sub, iva, descuento: desc, total: Math.max(0, base + iva) };
  }, [invForm.lineas, invForm.descuento, invForm.descuento_tipo]);

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-600";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";
  const TABS = [
    { k: "summary", label: t("acc_tab_summary"), icon: PieChart },
    { k: "invoices", label: t("acc_tab_invoices"), icon: FileText },
    { k: "payments", label: t("acc_tab_payments"), icon: Wallet },
    { k: "expenses", label: t("acc_tab_expenses"), icon: Receipt },
    { k: "aging", label: t("acc_tab_aging"), icon: AlertCircle },
    ...(isGlobal ? [{ k: "suppliers", label: t("acc_tab_suppliers"), icon: Truck }] : []),
    { k: "branches", label: t("acc_tab_branches"), icon: Building2 },
    { k: "statement", label: t("acc_tab_statement"), icon: ScrollText },
  ];

  return (
    <div className="space-y-5" data-testid="accounting-page">
      {isGlobal && <OrphansBanner api={api} t={t} oficinas={oficinas} onDone={load} />}
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Landmark className="w-5 h-5 text-indigo-600" /> {t("accounting")}
          </h2>
          <p className="text-sm text-slate-500">
            {isGlobal ? t("acc_scope_global") : `${t("office")}: ${user.oficina || "—"} · ${t("acc_scope_branch")}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="new-expense-btn" onClick={() => { setExpForm({ ...EMPTY_EXPENSE }); setModal("expense"); }} className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 rounded-lg font-semibold text-xs flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> {t("new_expense")}
          </button>
          <button data-testid="new-invoice-btn" onClick={() => { setEditingInvoice(null); setInvForm({ ...EMPTY_INVOICE, lineas: [{ descripcion: "", cantidad: 1, precio_unitario: 0, iva_pct: 21 }] }); setModal("invoice"); }} className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg font-semibold text-xs flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> {t("new_invoice")}
          </button>
          <button data-testid="new-payment-btn" onClick={() => { setPayForm({ ...EMPTY_PAYMENT }); setModal("payment"); }} className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg font-semibold text-xs flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> {t("new_payment")}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-end gap-3">
        <div><label className={lbl}>{t("from_date")}</label><input type="date" data-testid="acc-from" value={desde} onChange={(e) => setDesde(e.target.value)} className={inp} /></div>
        <div><label className={lbl}>{t("to_date")}</label><input type="date" data-testid="acc-to" value={hasta} onChange={(e) => setHasta(e.target.value)} className={inp} /></div>
        {isGlobal && (
          <div className="min-w-[180px]">
            <label className={lbl}>{t("office")}</label>
            <select data-testid="acc-office" value={oficinaF} onChange={(e) => setOficinaF(e.target.value)} className={inp}>
              <option value="">{t("all_offices")}</option>
              {oficinas.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 flex-wrap" data-testid="acc-tabs">
        {TABS.map(({ k, label, icon: Icon }) => (
          <button
            key={k}
            data-testid={`acc-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors ${tab === k ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-indigo-300"}`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {tab === "summary" && summary && (
        <div className="space-y-4" data-testid="acc-summary">
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-7 gap-3">
            <Stat testid="stat-invoiced" icon={FileText} label={t("invoiced")} value={money(summary.convertido?.facturado ?? summary.facturado)} tone="bg-indigo-50 border-indigo-200 text-indigo-900" />
            <Stat testid="stat-collected" icon={Wallet} label={t("collected")} value={money(summary.convertido?.cobrado ?? summary.cobrado)} tone="bg-emerald-50 border-emerald-200 text-emerald-900" />
            <Stat testid="stat-pending" icon={TrendingDown} label={t("us_owe") || "Nos deben"} value={money(summary.convertido?.pendiente_cobro ?? summary.pendiente_cobro)} tone="bg-amber-50 border-amber-200 text-amber-900" />
            <Stat testid="stat-debt" icon={TrendingUp} label={t("we_owe") || "Debemos"} value={money(summary.convertido?.pendiente_pago ?? summary.pendiente_pago ?? 0)} tone="bg-orange-50 border-orange-200 text-orange-900" />
            <Stat testid="stat-expenses" icon={Receipt} label={t("total_expenses")} value={money(summary.convertido?.gastos ?? summary.gastos)} tone="bg-red-50 border-red-200 text-red-900" />
            <Stat testid="stat-available" icon={Wallet} label={t("available") || "Disponible"} value={money(summary.convertido?.disponible ?? summary.disponible ?? 0)} tone="bg-teal-50 border-teal-200 text-teal-900" />
            <Stat testid="stat-profit" icon={TrendingUp} label={t("profit")} value={money(summary.convertido?.beneficio ?? summary.beneficio)} tone="bg-slate-900 border-slate-900 text-white" />
          </div>
          <p className="text-[11px] text-slate-400 -mt-2">{t("converted_total")} · {t("base_currency")}: <b>{cur}</b></p>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="acc-by-currency">
            <div className="px-4 py-3 border-b border-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500">{t("totals_by_currency")}</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                  <tr>
                    <th className="p-2.5 text-start">{t("currency")}</th>
                    <th className="p-2.5 text-end">{t("invoiced")}</th>
                    <th className="p-2.5 text-end">{t("collected")}</th>
                    <th className="p-2.5 text-end">{t("us_owe") || "Nos deben"}</th>
                    <th className="p-2.5 text-end">{t("we_owe") || "Debemos"}</th>
                    <th className="p-2.5 text-end">{t("total_expenses")}</th>
                    <th className="p-2.5 text-end">{t("available") || "Disponible"}</th>
                    <th className="p-2.5 text-end">{t("profit")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(summary.by_moneda || []).length === 0 && <tr><td colSpan={8} className="p-4 text-center text-slate-400 text-xs">—</td></tr>}
                  {(summary.by_moneda || []).map(r => (
                    <tr key={r.moneda} data-testid={`cur-row-${r.moneda}`}>
                      <td className="p-2.5 font-semibold font-mono">{r.moneda}</td>
                      <td className="p-2.5 text-end tabular-nums">{money(r.facturado, r.moneda)}</td>
                      <td className="p-2.5 text-end tabular-nums text-emerald-700">{money(r.cobrado, r.moneda)}</td>
                      <td className="p-2.5 text-end tabular-nums text-amber-700">{money(r.pendiente_cobro, r.moneda)}</td>
                      <td className="p-2.5 text-end tabular-nums text-orange-700">{money(r.pendiente_pago || 0, r.moneda)}</td>
                      <td className="p-2.5 text-end tabular-nums text-red-700">{money(r.gastos, r.moneda)}</td>
                      <td className={`p-2.5 text-end tabular-nums font-semibold ${(r.disponible || 0) >= 0 ? "text-teal-700" : "text-red-700"}`}>{money(r.disponible || 0, r.moneda)}</td>
                      <td className="p-2.5 text-end tabular-nums font-semibold">{money(r.beneficio, r.moneda)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {isGlobal && (
              <div className="border-t border-slate-100 p-3 bg-slate-50" data-testid="acc-rates">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">{t("exchange_rates")} · {t("rate_hint")} ({cur})</p>
                <div className="flex flex-wrap items-end gap-2">
                  {CURRENCIES.map(c => (
                    <div key={c.code} className="w-28">
                      <label className="block text-[10px] font-semibold text-slate-500 mb-1">{c.code}</label>
                      <input type="number" step="0.00001" data-testid={`rate-${c.code}`} value={rates[c.code] ?? ""} onChange={(e) => setRates({ ...rates, [c.code]: e.target.value })} className={inp} />
                    </div>
                  ))}
                  <button data-testid="save-rates" onClick={saveRates} className="bg-slate-900 text-white px-4 py-2 rounded-lg text-xs font-semibold">{t("save_rates")}</button>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500">{t("by_office")}</div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                  <tr><th className="p-2.5 text-start">{t("office")}</th><th className="p-2.5 text-end">{t("invoiced")}</th><th className="p-2.5 text-end">{t("collected")}</th><th className="p-2.5 text-end">{t("total_expenses")}</th><th className="p-2.5 text-end">{t("profit")}</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {summary.by_oficina.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-slate-400 text-xs">—</td></tr>}
                  {summary.by_oficina.map(r => (
                    <tr key={r.oficina}>
                      <td className="p-2.5 font-medium">{r.oficina}</td>
                      <td className="p-2.5 text-end tabular-nums">{money(r.facturado)}</td>
                      <td className="p-2.5 text-end tabular-nums text-emerald-700">{money(r.cobrado)}</td>
                      <td className="p-2.5 text-end tabular-nums text-red-700">{money(r.gastos)}</td>
                      <td className="p-2.5 text-end tabular-nums font-semibold">{money(r.beneficio)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500">{t("by_category")}</div>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  {summary.by_categoria.length === 0 && <tr><td className="p-4 text-center text-slate-400 text-xs">—</td></tr>}
                  {summary.by_categoria.map(r => (
                    <tr key={r.categoria}>
                      <td className="p-2.5 capitalize">{t(`exp_cat_${r.categoria}`) || r.categoria}</td>
                      <td className="p-2.5 text-end tabular-nums">{money(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "invoices" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="acc-invoices">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="p-3 text-start">{t("invoice_number")}</th>
                <th className="p-3 text-start">{t("date")}</th>
                <th className="p-3 text-start">{t("client")}</th>
                <th className="p-3 text-start">{t("office")}</th>
                <th className="p-3 text-end">{t("total")}</th>
                <th className="p-3 text-end">{t("collected")}</th>
                <th className="p-3 text-end">{t("balance")}</th>
                <th className="p-3 text-start">{t("status")}</th>
                <th className="p-3 text-start">{t("who")}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-slate-400">—</td></tr>}
              {invoices.map(i => (
                <tr key={i.id} data-testid={`inv-row-${i.numero}`}>
                  <td className="p-3 font-mono font-semibold">{i.numero}<span className="ms-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600" data-testid={`inv-cur-${i.numero}`}>{i.moneda || cur}</span></td>
                  <td className="p-3 tabular-nums text-slate-600">{i.fecha}</td>
                  <td className="p-3">{i.cliente_nombre || "—"}</td>
                  <td className="p-3 text-slate-600">{i.oficina || "—"}</td>
                  <td className="p-3 text-end tabular-nums font-semibold">{money(i.total, i.moneda)}</td>
                  <td className="p-3 text-end tabular-nums text-emerald-700">{money(i.pagado, i.moneda)}</td>
                  <td className="p-3 text-end tabular-nums">{money(i.saldo, i.moneda)}</td>
                  <td className="p-3">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${i.estado === "pagada" ? "bg-emerald-100 text-emerald-700" : i.estado === "parcial" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                      {t(`inv_status_${i.estado}`) || i.estado}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-slate-500">{i.created_by_name || "—"}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      <PdfLangMenu testid={`inv-pdf-${i.numero}`} onPrint={(lg) => invoicePDF(i, lg, i.moneda || cur, settings)} label="PDF" />
                      {isGlobal && <button data-testid={`inv-edit-${i.numero}`} onClick={() => openInvoiceEdit(i)} className="p-1.5 text-slate-400 hover:text-indigo-600 rounded"><Pencil className="w-4 h-4" /></button>}
                      {isGlobal && <button data-testid={`inv-del-${i.numero}`} onClick={() => del("invoices", i.id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "payments" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="acc-payments">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="p-3 text-start">{t("date")}</th>
                <th className="p-3 text-start">{t("invoice_number")}</th>
                <th className="p-3 text-start">{t("client")}</th>
                <th className="p-3 text-start">{t("office")}</th>
                <th className="p-3 text-start">{t("payment_method")}</th>
                <th className="p-3 text-end">{t("amount")}</th>
                <th className="p-3 text-start">{t("notes")}</th>
                <th className="p-3 text-start">{t("attachment") || "Adjunto"}</th>
                <th className="p-3 text-start">{t("who")}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payments.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-slate-400">—</td></tr>}
              {payments.map(p => (
                <tr key={p.id} data-testid={`pay-row-${p.id}`}>
                  <td className="p-3 tabular-nums text-slate-600">{p.fecha}</td>
                  <td className="p-3 font-mono">{p.invoice_numero || "—"}</td>
                  <td className="p-3">{p.cliente_nombre || "—"}</td>
                  <td className="p-3 text-slate-600">{p.oficina || "—"}</td>
                  <td className="p-3 capitalize text-slate-600">{t(`pay_${p.metodo}`) || p.metodo}</td>
                  <td className="p-3 text-end tabular-nums font-semibold text-emerald-700">{money(p.monto, p.moneda)}</td>
                  <td className="p-3 text-xs text-slate-600 max-w-[220px]" title={p.notas || p.referencia || ""} data-testid={`pay-notas-${p.id}`}>
                    {(p.notas || p.referencia) ? <span className="line-clamp-2 whitespace-pre-wrap break-words">{p.notas || p.referencia}</span> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="p-3">
                    <AttachmentThumb api={api} token={token} ids={p.photo_ids || []} testid={`pay-att-${p.id}`} />
                  </td>
                  <td className="p-3 text-xs text-slate-500">{p.created_by_name || "—"}</td>
                  <td className="p-3 text-end">
                    <div className="inline-flex items-center gap-1">
                      <PdfLangMenu
                        testid={`pay-pdf-${p.id}`}
                        compact
                        icon={<FileText className="w-4 h-4" />}
                        onPrint={(lg) => downloadVoucher(p, "payment", { money, emisor: settings?.emisor || {}, lang: lg })}
                      />
                      {isGlobal && <button data-testid={`pay-del-${p.id}`} onClick={() => del("payments", p.id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "expenses" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="acc-expenses">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="p-3 text-start">{t("date")}</th>
                <th className="p-3 text-start">{t("category")}</th>
                <th className="p-3 text-start">{t("description")}</th>
                <th className="p-3 text-start">{t("supplier")}</th>
                <th className="p-3 text-start">{t("office")}</th>
                <th className="p-3 text-end">{t("amount")}</th>
                <th className="p-3 text-end">{t("total")}</th>
                <th className="p-3 text-start">{t("notes")}</th>
                <th className="p-3 text-start">{t("attachment") || "Adjunto"}</th>
                <th className="p-3 text-start">{t("who")}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {expenses.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-slate-400">—</td></tr>}
              {expenses.map(e => (
                <tr key={e.id} data-testid={`exp-row-${e.id}`}>
                  <td className="p-3 tabular-nums text-slate-600">{e.fecha}</td>
                  <td className="p-3"><span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t(`exp_cat_${e.categoria}`) || e.categoria}</span></td>
                  <td className="p-3">{e.descripcion || "—"}</td>
                  <td className="p-3 text-slate-600">{e.proveedor || "—"}</td>
                  <td className="p-3 text-slate-600">{e.oficina || "—"}</td>
                  <td className="p-3 text-end tabular-nums">{money(e.monto, e.moneda)}</td>
                  <td className="p-3 text-end tabular-nums font-semibold text-red-700">{money(e.total, e.moneda)}</td>
                  <td className="p-3 text-xs text-slate-600 max-w-[220px]" title={e.notas || ""} data-testid={`exp-notas-${e.id}`}>
                    {e.notas ? <span className="line-clamp-2 whitespace-pre-wrap break-words">{e.notas}</span> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="p-3">
                    <AttachmentThumb api={api} token={token} ids={e.photo_ids || []} testid={`exp-att-${e.id}`} />
                  </td>
                  <td className="p-3 text-xs text-slate-500">{e.created_by_name || "—"}</td>
                  <td className="p-3 text-end">
                    <div className="inline-flex items-center gap-1">
                      <PdfLangMenu
                        testid={`exp-pdf-${e.id}`}
                        compact
                        icon={<FileText className="w-4 h-4" />}
                        onPrint={(lg) => downloadVoucher(e, "expense", { money, emisor: settings?.emisor || {}, lang: lg })}
                      />
                      {isGlobal && <button data-testid={`exp-del-${e.id}`} onClick={() => del("expenses", e.id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "aging" && <AgingTab isGlobal={isGlobal} oficinas={oficinas} />}
      {tab === "suppliers" && isGlobal && <SuppliersTab oficinas={oficinas} currency={cur} />}
      {tab === "branches" && <BranchesTab isGlobal={isGlobal} oficinas={oficinas} />}
      {tab === "statement" && <StatementTab isGlobal={isGlobal} oficinas={oficinas} clients={clients} settings={settings} />}

      {/* ==== Expense modal ==== */}
      {modal === "expense" && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={saveExpense} className="bg-white rounded-xl w-full max-w-lg p-6 space-y-3" data-testid="expense-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Receipt className="w-5 h-5 text-slate-700" /> {t("new_expense")}</h3>
              <button type="button" onClick={() => setModal(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>{t("date")}</label><input required type="date" data-testid="exp-fecha" value={expForm.fecha} onChange={(e) => setExpForm({ ...expForm, fecha: e.target.value })} className={inp} /></div>
              <div>
                <label className={lbl}>{t("category")}</label>
                <select data-testid="exp-categoria" value={expForm.categoria} onChange={(e) => setExpForm({ ...expForm, categoria: e.target.value })} className={inp}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{t(`exp_cat_${c}`) || c}</option>)}
                </select>
              </div>
            </div>
            <div><label className={lbl}>{t("description")}</label><input required data-testid="exp-desc" value={expForm.descripcion} onChange={(e) => setExpForm({ ...expForm, descripcion: e.target.value })} className={inp} /></div>
            <div><label className={lbl}>{t("supplier")}</label><input data-testid="exp-proveedor" value={expForm.proveedor} onChange={(e) => setExpForm({ ...expForm, proveedor: e.target.value })} className={inp} /></div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><label className={lbl}>{t("amount")}</label><input required type="number" step="0.01" data-testid="exp-monto" value={expForm.monto} onChange={(e) => setExpForm({ ...expForm, monto: +e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("currency")}</label><CurrencySelect testid="exp-moneda" value={expForm.moneda} onChange={(v) => setExpForm({ ...expForm, moneda: v })} /></div>
              <div><label className={lbl}>{t("vat")}</label><input type="number" step="0.5" data-testid="exp-iva" value={expForm.iva_pct} onChange={(e) => setExpForm({ ...expForm, iva_pct: +e.target.value })} className={inp} /></div>
              <div>
                <label className={lbl}>{t("payment_method")}</label>
                <select data-testid="exp-metodo" value={expForm.metodo_pago} onChange={(e) => setExpForm({ ...expForm, metodo_pago: e.target.value })} className={inp}>
                  {METHODS.map(m => <option key={m} value={m}>{t(`pay_${m}`) || m}</option>)}
                </select>
              </div>
            </div>
            {isGlobal && (
              <div>
                <label className={lbl}>
                  {t("office")} <span className="text-red-600">*</span>
                </label>
                <select
                  required
                  data-testid="exp-oficina"
                  value={expForm.oficina}
                  onChange={(e) => setExpForm({ ...expForm, oficina: e.target.value })}
                  className={inp}
                >
                  <option value="">— {t("select_office") || "Elige la oficina"} —</option>
                  {oficinas.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <p className="text-[11px] text-slate-500 mt-1">
                  {t("exp_office_hint") || "El gasto sólo aparece en la contabilidad de la oficina que elijas."}
                </p>
              </div>
            )}
            <div>
              <PhotoUploader
                scope="expense" scopeId=""
                value={expForm.photo_ids || []}
                onChange={(ids) => setExpForm({ ...expForm, photo_ids: ids })}
                max={4}
                label={t("attach_receipt") || "Adjuntar factura / recibo"}
                accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
                testid="exp-attach"
              />
            </div>
            <div className="text-xs text-slate-500 text-end">{t("total")}: <b className="tabular-nums">{money((+expForm.monto || 0) * (1 + (+expForm.iva_pct || 0) / 100), expForm.moneda)}</b></div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setModal(null)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" disabled={busy} data-testid="exp-submit" className="flex-1 bg-slate-900 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
        </div>
      )}

      {/* ==== Invoice modal ==== */}
      {modal === "invoice" && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={saveInvoice} className="bg-white rounded-xl w-full max-w-2xl p-6 space-y-3 max-h-[92vh] overflow-y-auto" data-testid="invoice-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-600" /> {editingInvoice ? t("edit_invoice") : t("new_invoice")}</h3>
              <button type="button" onClick={() => { setModal(null); setEditingInvoice(null); }}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>{t("client")}</label>
                <select data-testid="inv-client" value={invForm.client_id} onChange={(e) => {
                  const c = clients.find(x => x.id === e.target.value);
                  setInvForm({
                    ...invForm,
                    client_id: e.target.value,
                    cliente_nombre: c ? (c.empresa || c.fullname) : "",
                    cliente_tax_id: c?.rfc_nit || "",
                    cliente_direccion: c ? [c.direccion, c.ciudad, c.provincia, c.pais].filter(Boolean).join(", ") : "",
                  });
                }} className={inp}>
                  <option value="">—</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.fullname} {c.empresa ? `(${c.empresa})` : ""}</option>)}
                </select>
              </div>
              <div><label className={lbl}>{t("fullname")}</label><input required data-testid="inv-nombre" value={invForm.cliente_nombre} onChange={(e) => setInvForm({ ...invForm, cliente_nombre: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("tax_id")}</label><input data-testid="inv-taxid" value={invForm.cliente_tax_id} onChange={(e) => setInvForm({ ...invForm, cliente_tax_id: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("address")}</label><input data-testid="inv-dir" value={invForm.cliente_direccion} onChange={(e) => setInvForm({ ...invForm, cliente_direccion: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("date")}</label><input required type="date" data-testid="inv-fecha" value={invForm.fecha} onChange={(e) => setInvForm({ ...invForm, fecha: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("due_date")}</label><input type="date" data-testid="inv-venc" value={invForm.vencimiento} onChange={(e) => setInvForm({ ...invForm, vencimiento: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("currency")}</label><CurrencySelect testid="inv-moneda" value={invForm.moneda} onChange={(v) => setInvForm({ ...invForm, moneda: v })} /></div>
              <div>
                <label className={lbl}>{t("payment_method")}</label>
                <select data-testid="inv-metodo" value={invForm.metodo_pago} onChange={(e) => setInvForm({ ...invForm, metodo_pago: e.target.value })} className={inp}>
                  {METHODS.map(m => <option key={m} value={m}>{t(`pay_${m}`) || m}</option>)}
                  <option value="credito">{t("pay_credito") || "Crédito"}</option>
                </select>
              </div>
              {isGlobal && (
                <div className="col-span-2">
                  <label className={lbl}>{t("office")}</label>
                  <select data-testid="inv-oficina" value={invForm.oficina} onChange={(e) => setInvForm({ ...invForm, oficina: e.target.value })} className={inp}>
                    <option value="">—</option>
                    {oficinas.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div className="border-2 border-indigo-200 rounded-lg p-3 bg-indigo-50/40">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-indigo-900">{t("invoice_lines")}</p>
                <button type="button" data-testid="inv-add-line" onClick={() => setInvForm(f => ({ ...f, lineas: [...f.lineas, { _uid: `l-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, descripcion: "", cantidad: 1, precio_unitario: 0, iva_pct: 21 }] }))} className="bg-indigo-600 text-white text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1">
                  <Plus className="w-3 h-3" /> {t("add")}
                </button>
              </div>
              <div className="space-y-2">
                {invForm.lineas.map((l, idx) => (
                  <div key={l._uid || `line-${idx}`} className="bg-white border border-indigo-200 rounded-md p-2 grid grid-cols-[1fr_60px_90px_60px_28px] gap-2 items-center">
                    <input required placeholder={t("description")} data-testid={`inv-line-${idx}-desc`} value={l.descripcion} onChange={(e) => setInvForm(f => { const a = [...f.lineas]; a[idx] = { ...a[idx], descripcion: e.target.value }; return { ...f, lineas: a }; })} className={inp} />
                    <input type="number" step="0.01" title={t("qty")} data-testid={`inv-line-${idx}-qty`} value={l.cantidad} onChange={(e) => setInvForm(f => { const a = [...f.lineas]; a[idx] = { ...a[idx], cantidad: +e.target.value }; return { ...f, lineas: a }; })} className={`${inp} tabular-nums`} />
                    <input type="number" step="0.01" title={t("unit_price")} data-testid={`inv-line-${idx}-price`} value={l.precio_unitario} onChange={(e) => setInvForm(f => { const a = [...f.lineas]; a[idx] = { ...a[idx], precio_unitario: +e.target.value }; return { ...f, lineas: a }; })} className={`${inp} tabular-nums`} />
                    <input type="number" step="0.5" title={t("vat")} data-testid={`inv-line-${idx}-vat`} value={l.iva_pct} onChange={(e) => setInvForm(f => { const a = [...f.lineas]; a[idx] = { ...a[idx], iva_pct: +e.target.value }; return { ...f, lineas: a }; })} className={`${inp} tabular-nums`} />
                    <button type="button" data-testid={`inv-line-${idx}-remove`} onClick={() => setInvForm(f => ({ ...f, lineas: f.lineas.filter((_, i) => i !== idx) }))} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-indigo-200 flex justify-end items-center gap-4 text-xs text-indigo-900 flex-wrap">
                <label className="flex items-center gap-2 text-indigo-900">
                  <span className="font-semibold">{t("discount") || "Descuento"}:</span>
                  <div className="inline-flex items-stretch border border-indigo-300 rounded overflow-hidden">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      data-testid="inv-descuento"
                      value={invForm.descuento}
                      onChange={(e) => setInvForm({ ...invForm, descuento: +e.target.value })}
                      className="w-20 px-2 py-1 text-sm tabular-nums outline-none border-0 focus:ring-0"
                    />
                    <button
                      type="button"
                      data-testid="inv-descuento-tipo"
                      onClick={() => setInvForm({ ...invForm, descuento_tipo: invForm.descuento_tipo === "percent" ? "amount" : "percent" })}
                      title={invForm.descuento_tipo === "percent" ? t("switch_to_amount") || "Cambiar a importe" : t("switch_to_percent") || "Cambiar a porcentaje"}
                      className="px-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs border-s border-indigo-300 min-w-[32px]"
                    >
                      {invForm.descuento_tipo === "percent" ? "%" : "€"}
                    </button>
                  </div>
                  {invForm.descuento_tipo === "percent" && invTotals.descuento > 0 && (
                    <span className="text-[10px] text-indigo-500">(= {money(invTotals.descuento, invForm.moneda)})</span>
                  )}
                </label>
                <span>{t("subtotal")}: <b className="tabular-nums">{money(invTotals.sub, invForm.moneda)}</b></span>
                <span>{t("vat")}: <b className="tabular-nums">{money(invTotals.iva, invForm.moneda)}</b></span>
                <span>{t("total")}: <b className="tabular-nums" data-testid="inv-total">{money(invTotals.total, invForm.moneda)}</b></span>
              </div>
            </div>

            <div><label className={lbl}>{t("notes")}</label><textarea rows={3} data-testid="inv-notas" value={invForm.notas} onChange={(e) => setInvForm({ ...invForm, notas: e.target.value })} placeholder={t("notes_placeholder") || "Añade más información visible en la factura…"} className={inp} /></div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={lbl + " !mb-0"}>{t("remarks")}</label>
                {settings.remarks_default && !invForm.remarks && (
                  <button
                    type="button"
                    data-testid="inv-remarks-fill"
                    onClick={() => setInvForm({ ...invForm, remarks: settings.remarks_default })}
                    className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 uppercase tracking-wider"
                  >
                    {t("use_default_remarks") || "Usar predeterminado"}
                  </button>
                )}
              </div>
              <textarea rows={3} data-testid="inv-remarks" value={invForm.remarks} onChange={(e) => setInvForm({ ...invForm, remarks: e.target.value })} placeholder={settings.remarks_default ? (settings.remarks_default.slice(0, 120) + (settings.remarks_default.length > 120 ? "…" : "")) : ""} className={inp} />
              <p className="text-[10px] text-slate-400 mt-1">{t("remarks_hint")}</p>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setEditingInvoice(null); }} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" disabled={busy} data-testid="inv-submit" className="flex-1 bg-indigo-600 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
        </div>
      )}

      {/* ==== Payment modal ==== */}
      {modal === "payment" && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={savePayment} className="bg-white rounded-xl w-full max-w-md p-6 space-y-3" data-testid="payment-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-600" /> {t("new_payment")}</h3>
              <button type="button" onClick={() => setModal(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div>
              <label className={lbl}>{t("invoice")}</label>
              <select data-testid="pay-invoice" value={payForm.invoice_id} onChange={(e) => {
                const inv = invoices.find(x => x.id === e.target.value);
                setPayForm({ ...payForm, invoice_id: e.target.value, monto: inv ? inv.saldo : payForm.monto, moneda: inv ? (inv.moneda || cur) : payForm.moneda });
              }} className={inp}>
                <option value="">—</option>
                {openInvoices.map(i => (
                  <option key={i.id} value={i.id}>{i.numero} · {i.cliente_nombre} · {t("balance")} {money(i.saldo, i.moneda)}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>{t("date")}</label><input required type="date" data-testid="pay-fecha" value={payForm.fecha} onChange={(e) => setPayForm({ ...payForm, fecha: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("amount")}</label><input required type="number" step="0.01" data-testid="pay-monto" value={payForm.monto} onChange={(e) => setPayForm({ ...payForm, monto: +e.target.value })} className={inp} /></div>
              <div className="col-span-2"><label className={lbl}>{t("currency")}</label><CurrencySelect testid="pay-moneda" value={payForm.moneda} onChange={(v) => setPayForm({ ...payForm, moneda: v })} disabled={!!payForm.invoice_id} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>{t("payment_method")}</label>
                <select data-testid="pay-metodo" value={payForm.metodo} onChange={(e) => setPayForm({ ...payForm, metodo: e.target.value })} className={inp}>
                  {METHODS.map(m => <option key={m} value={m}>{t(`pay_${m}`) || m}</option>)}
                </select>
              </div>
              <div><label className={lbl}>{t("reference")}</label><input data-testid="pay-ref" value={payForm.referencia} onChange={(e) => setPayForm({ ...payForm, referencia: e.target.value })} className={inp} /></div>
            </div>
            <div>
              <PhotoUploader
                scope="payment" scopeId=""
                value={payForm.photo_ids || []}
                onChange={(ids) => setPayForm({ ...payForm, photo_ids: ids })}
                max={4}
                label={t("attach_receipt") || "Adjuntar factura / recibo"}
                accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
                testid="pay-attach"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setModal(null)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" disabled={busy} data-testid="pay-submit" className="flex-1 bg-emerald-600 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


/**
 * OrphansBanner — surfaces records with empty oficina and lets the admin
 * bulk-reassign them to a real branch. Silent when everything is clean.
 */
function OrphansBanner({ api, t, oficinas, onDone }) {
  const [counts, setCounts] = React.useState(null);
  const [target, setTarget] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const refresh = React.useCallback(async () => {
    try { const r = await api.get("/accounting/orphans"); setCounts(r.data); }
    catch { /* endpoint absent or unauthorized — silently hide */ }
  }, [api]);

  React.useEffect(() => { refresh(); }, [refresh]);

  if (!counts) return null;
  const total = (counts.expenses || 0) + (counts.invoices || 0) + (counts.payments || 0);
  if (total === 0) return null;

  const adopt = async () => {
    if (!target) { toast.error(t("select_office") || "Elige la oficina"); return; }
    setBusy(true);
    try {
      await api.post("/accounting/orphans/adopt", null, { params: { target_oficina: target } });
      toast.success(t("saved"));
      setCounts(null);
      onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="acc-orphans-banner">
      <div className="flex items-start gap-3 mb-2">
        <div className="w-8 h-8 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0">!</div>
        <div className="flex-1">
          <h4 className="font-semibold text-sm text-amber-900">
            {t("orphans_title") || "Hay registros contables sin oficina asignada"}
          </h4>
          <p className="text-xs text-amber-800 mt-1">
            {counts.expenses} {t("expenses") || "gastos"} · {counts.invoices} {t("invoices") || "facturas"} · {counts.payments} {t("payments") || "cobros"}. {t("orphans_hint") || "Ninguna sucursal los ve. Asigna una oficina para que aparezcan en su contabilidad."}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap ps-11">
        <select
          data-testid="acc-orphans-target"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="px-2 py-1.5 border border-amber-300 rounded-md bg-white text-sm"
        >
          <option value="">— {t("select_office") || "Elige la oficina"} —</option>
          {(oficinas || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <button
          onClick={adopt}
          disabled={busy || !target}
          data-testid="acc-orphans-adopt"
          className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-semibold px-3 py-1.5 rounded-md text-xs"
        >
          {busy ? "…" : (t("orphans_adopt") || "Adoptar en esa oficina")}
        </button>
      </div>
    </div>
  );
}
